/**
 * Agent trace store — canonical types.
 *
 * The trace is the agent turn: one `agent_turns` row per user-send (correlated by
 * `traceId`), N `tool_calls` per turn, and `error_events` as the durable error
 * surface. These TS shapes are the single source of truth; the server's
 * `agent-trace.service` maps them to the snake_case DB rows. Metadata-first —
 * no prompt/response/tool content lives here in v1 (see the design spec).
 *
 * Kept in sync with `packages/shared/src/schemas/agent-trace.schema.ts` and the
 * `20260625120000_agent_trace_store.sql` migration.
 */

/** Per-user-send correlation id (UUID v4). */
export type TraceId = string;

/** Which runtime produced the turn. */
export type Arm = 'claude' | 'native' | 'openai';

/** Which client surface originated the turn (OD2, omni-observability).
 *  Orthogonal to `Arm` by design: arm is which engine ran; surface is where
 *  the user was. 'routine'/'probe' are server-origin values (scheduled work /
 *  synthetic probes) — no interactive client sends them. 'api' is a turn a
 *  company's server sent over /v1 for one of its people (the platform API).
 *  Distinct from the USER_MESSAGE `surface` routing field ('web'|'native'|
 *  'channel'), which gates runner placement and must never be widened. */
export type Surface =
  | 'web'
  | 'native-app'
  | 'notch'
  | 'extension'
  | 'os-hands'
  | 'cli'
  | 'routine'
  | 'probe'
  | 'api';

/** Lifecycle/terminal state of a turn. `running` = started, not yet finalized.
 *  `paused` = the turn ended because the agent suspended for a human-in-the-loop
 *  interaction (it is neither a success nor a failure; the turn resumes on resolve).
 *  `disconnected` = the turn was cut without a sandbox AGENT_END (WS drop / deploy /
 *  crash / stranded accumulator) — the outcome is unknown, not a failure. The DB
 *  column is unconstrained text, so no migration accompanies this member. */
export type TerminalStatus = 'running' | 'ok' | 'stopped' | 'failed' | 'paused' | 'disconnected';

/** Which tier emitted an error event. */
export type ErrorSource = 'web' | 'server' | 'agent';

export type ErrorSeverity = 'warn' | 'error' | 'fatal';

/**
 * Mint a fresh trace id. Web Crypto global — available in Node 20+ and all
 * supported browsers (the web app already uses `crypto.randomUUID()` on send).
 */
export function mintTraceId(): TraceId {
  return globalThis.crypto.randomUUID();
}

/** One row per user-send agent run — the canonical wide event. */
export interface AgentTurnRecord {
  traceId: TraceId;
  sdkSessionId?: string;
  taskId: string;
  userId?: string;
  rootTaskId?: string;
  spawnDepth?: number;
  agentSlug?: string;
  arm?: Arm;
  surface?: Surface;

  modelRequested?: string;
  modelResponded?: string;
  provider?: string;

  inputTokens?: number;
  outputTokens?: number;
  cacheReadTokens?: number;
  cacheCreationTokens?: number;
  reasoningTokens?: number;
  costUsd?: number;

  /** Media-refs S0 — MAX per-call request size / image load across the turn. */
  requestBytes?: number;
  imageCount?: number;
  imageBytes?: number;

  ttftMs?: number;
  durationMs?: number;

  finishReason?: string;
  terminalReasonKind?: string;
  terminalStatus?: TerminalStatus;
  errorClass?: string;
  leakedErrorText?: string;
  /** A turn the SDK reported `success` for, that nonetheless failed (billed-but-empty). */
  isSilentFailure?: boolean;

  numToolCalls?: number;
  numTurns?: number;

  /** Orchestration walls (spec 2026-09-02) — the deepest helper this turn reached,
   *  and how many spawns its walls refused (every refusal kind summed). Undefined on
   *  every turn that reported no tally, which the row builder spread-omits. */
  spawnMaxDepth?: number;
  spawnRefused?: number;
}

/** One row per tool invocation within a turn. */
export interface ToolCallRecord {
  traceId: TraceId;
  taskId?: string;
  toolUseId?: string;
  toolName: string;
  seq?: number;
  /** `null` — the call PAUSED for a human-in-the-loop interaction (neither a
   *  success nor a failure; error_class is 'paused'). `true`/`false` otherwise. */
  success?: boolean | null;
  errorClass?: string;
  durationMs?: number;
  calledNonexistentTool?: boolean;
  malformedArgs?: boolean;
  /**
   * Slice 2b: when this row is a nested in-code call dispatched by `execute_code`
   * (Programmatic Tool Calling), the `toolUseId` of the parent `execute_code`
   * invocation. Links the nested `tool_calls` row to its parent. Undefined for
   * ordinary (top-level) tool calls.
   */
  parentToolUseId?: string;
  /**
   * Adoption metric (spec §7): for an ability-provided Skill invocation, the
   * ability slug parsed from the plugin-namespaced skill name
   * (`<ability-slug>:<skill-slug>`). Undefined for every other tool_call
   * (the `ability_slug` column stays null).
   */
  abilitySlug?: string;
  /**
   * The REAL Composio action slugs an executor meta-call ran, uppercased, in payload
   * order — `['GMAIL_SEND_EMAIL']` for a send laundered through
   * `COMPOSIO_MULTI_EXECUTE_TOOL`. Undefined for every other tool call, and also for an
   * executor whose `tools[]` payload could not be read (the `inner_tool_slugs` column
   * stays NULL, which reads as "not recorded" rather than "ran nothing").
   *
   * `toolName` deliberately keeps the WRAPPER name. This is an additional fact about the
   * row, not a correction to it: rewriting `toolName` would silently change what every
   * existing aggregate over `tool_calls` counts, across the deploy boundary.
   */
  innerToolSlugs?: string[];
  /**
   * How each of those inner actions ENDED, as Composio's own result reported it: an executor
   * meta-call that succeeded as a whole can still carry a failed action inside (X answering 402,
   * staging 2026-10-16), and the wrapper's `success` cannot say so. Names and outcomes only,
   * never a payload or a person's text (analytics reads the column). Written to `result_redacted`
   * as `{ inner_tools: [{ tool_slug, successful, status_code?, problem? }] }`; undefined when the
   * result was not an executor's or could not be read.
   */
  innerToolOutcomes?: InnerToolOutcome[];
}

/** One inner Composio action's outcome: Composio's `successful`, and the HTTP status its vendor answered when it failed. */
export interface InnerToolOutcome {
  /** Uppercase action slug, e.g. TWITTER_RECENT_SEARCH. */
  slug: string;
  successful: boolean;
  /** `response.data.status_code` on a failed action, when Composio carried one. */
  statusCode?: number;
  /** A failed action's problem identifier from the vendor's JSON error body (its `type`, else its `code`), one token, never the body. */
  problem?: string;
}

/** Durable error surface across tiers; the source of truth for error-rate alerting. */
export interface ErrorEventRecord {
  traceId?: string;
  taskId?: string;
  userId?: string;
  category: string;
  errorClass?: string;
  messageRedacted?: string;
  terminalReasonKind?: string;
  source: ErrorSource;
  severity?: ErrorSeverity;
  commitSha?: string;
}
